import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'

import styles from './LandingStepsBlock.module.scss'

/*
  steps_section: numbered "how it works" cards. Items may carry an explicit
  number or rely on their position, and highlights are either plain strings or
  { icon, text } objects.
*/
type StepHighlight = string | { icon?: string; text?: string }

interface StepItem {
  number?: string | number;
  step?: string | number;
  title?: string;
  text?: string;
  icon?: string;
  highlights?: StepHighlight[];
}

interface StepsPayload {
  title?: string;
  subTitle?: string;
  subtitle?: string;
  items?: StepItem[];
}

export default function LandingStepsBlock ({ payload }: LandingBlockProps) {
  const { title, subTitle, subtitle, items } = (payload ?? {}) as StepsPayload

  const steps = (items ?? []).filter((item) => item?.title || item?.text)
  const caption = subTitle || subtitle

  if (steps.length === 0) {
    return null
  }

  return (
    <section className={styles.root}>
      {title && <h2 className={styles.root__title}>{title}</h2>}
      {caption && <p className={styles.root__subtitle}>{caption}</p>}

      <div className={styles.root__grid}>
        {steps.map((step, index) => (
          <article className={styles.card} key={index}>
            <span className={styles.card__number}>
              {step.number ?? step.step ?? index + 1}
            </span>

            {step.icon && (
              <img className={styles.card__icon} src={step.icon} alt="" loading="lazy" />
            )}

            {step.title && <h3 className={styles.card__title}>{step.title}</h3>}
            {step.text && <p className={styles.card__text}>{step.text}</p>}

            {Array.isArray(step.highlights) && step.highlights.length > 0 && (
              <ul className={styles.card__highlights}>
                {step.highlights.map((highlight, hIndex) => {
                  const isText = typeof highlight === 'string'
                  const text = isText ? highlight : highlight?.text
                  const icon = isText ? undefined : highlight?.icon

                  if (!text) {
                    return null
                  }

                  return (
                    <li className={styles.card__highlight} key={hIndex}>
                      {icon && (
                        <img
                          className={styles.card__highlightIcon}
                          src={icon}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      {text}
                    </li>
                  )
                })}
              </ul>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}
